// Refonte du suivi, lot 5 : valider puis faire sortir une version.
//
// Ordre, pour chaque canal :
//   suivi_valider_version (fige la version, une seule fois)
//   → suivi_verifier_publication_possible (tout ce qui peut échouer en base
//     échoue AVANT l'envoi)
//   → envoi externe (app : /api/admin/publish-plan via le proxy ; document :
//     PDF généré dans le navigateur)
//   → suivi_marquer_publiee / suivi_marquer_remise_document.
// « Réessayer » rejoue la même suite : la validation est idempotente, la
// vérification répond deja_fait quand la sortie est déjà notée, et le livrable
// app est reconstruit depuis la version figée puis comparé à l'empreinte
// validée : c'est le MÊME artefact, ou rien ne part.

import { clientAppFetch as defaultClientAppFetch } from '../../services/clientAppFetch';
import { supabase as defaultDb } from '../../supabaseClient';
import { normalizeNutritionConsultationCloudRow } from '../../services/nutritionConsultationPersistence';
import { construireLivrableApp, empreinteLivrable, ligneTelleQuePubliee } from './suiviControle';
import {
  SuiviErreur,
  lireVersion,
  marquerPubliee,
  marquerRemiseDocument,
  validerVersion,
  verifierPublicationPossible,
} from './suiviVersions';

export const CANAUX_DOCUMENT = Object.freeze(['email', 'hand_delivery', 'post']);
export const LIBELLES_CANAL_DOCUMENT = Object.freeze({
  email: 'Je l’envoie par e-mail',
  hand_delivery: 'Je le remets en main propre',
  post: 'Je l’envoie par la poste',
});

function erreurSortie(message, nature = 'refus') {
  return new SuiviErreur(message, { nature });
}

async function sha256Octets(octets) {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', octets);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Valide la version (idempotent : un rejeu identique rend la même validation). */
export async function validerSuivi({ ligne, controle, canaux, override = null }, { db = defaultDb } = {}) {
  if (!controle || controle.erreurLivrable) {
    throw erreurSortie(controle?.erreurLivrable || 'Le contrôle doit être relancé.');
  }
  return validerVersion({
    consultationId: ligne.id,
    revision: ligne.followup_edit_revision,
    contentSha256: controle.contentSha256,
    appArtifactSha256: canaux.includes('app') ? controle.appArtifactSha256 : null,
    canaux,
    verdict: controle.verdict,
    renderInputsSha256: controle.renderInputsSha256,
    override,
  }, { db });
}

/** Réponse de l'app déjà notée par le serveur pour cette validation, ou null. */
export async function lireReponseNotee(validationId, { db = defaultDb } = {}) {
  try {
    const { data, error } = await db.from('suivi_reponses_app').select('*').eq('validation_id', validationId).maybeSingle();
    return error ? null : (data || null);
  } catch {
    return null;
  }
}

/**
 * Publie dans l'app la version validée. La publication n'est enregistrée
 * qu'avec la réponse de l'app NOTÉE PAR LE SERVEUR (proxy) : si elle existe
 * déjà (coupure après l'envoi), rien n'est renvoyé à l'app.
 */
export async function publierDansApp({ client, validation }, {
  db,
  fetchClientApp = defaultClientAppFetch,
} = {}) {
  const controle = await verifierPublicationPossible({ validationId: validation.id, canal: 'app' }, { db });
  if (controle?.deja_fait === true) return { dejaFait: true };

  let notee = await lireReponseNotee(validation.id, { db });
  if (!notee) {
    const figee = await lireVersion(validation.consultation_id, { db });
    const payload = construireLivrableApp(client, ligneTelleQuePubliee(figee));
    const artifactSha256 = await empreinteLivrable(payload);
    if (artifactSha256 !== validation.app_artifact_sha256) {
      throw erreurSortie(
        'Le contenu de l’app ne correspond plus à la version validée (la fiche de la cliente a changé). Prépare une nouvelle version.',
      );
    }
    let reponse;
    try {
      reponse = await fetchClientApp('/api/admin/publish-plan', {
        method: 'POST',
        payload,
        proof: {
          artifact_sha256: artifactSha256,
          client_id: String(validation.client_id),
          consultation_id: String(validation.consultation_id),
          suivi_validation_id: String(validation.id),
        },
      });
    } catch (error) {
      throw erreurSortie(
        error?.payload?.error || error?.message || 'L’app n’a pas répondu.',
        error?.status ? 'refus' : 'reseau',
      );
    }
    if (reponse?.ok !== true) throw erreurSortie(reponse?.error || 'L’app n’a pas confirmé la publication.');
    notee = reponse.suivi_reponse_notee || null;
    if (!notee) {
      throw erreurSortie('L’app a répondu, mais sa réponse n’a pas pu être notée. Réessaie dans un instant.');
    }
  }

  const enregistree = await marquerPubliee({
    validationId: validation.id,
    appPlanId: notee.app_plan_id,
    publishedVersion: notee.published_version,
    artifactSha256: notee.artifact_sha256,
    publishedAt: notee.published_at,
  }, { db });
  return { dejaFait: false, validation: enregistree, reponse: notee };
}

/** Génère le PDF client (moteur jsPDF existant de nutritionPdf.js). */
async function genererPdfParDefaut(consultation, client) {
  const { buildConsultationPdfBlob } = await import('../../nutritionPdf');
  return buildConsultationPdfBlob(consultation, client);
}

function telechargerParDefaut(blob, nom) {
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function nomFichierPdf(client, ligne) {
  const prenom = String(client?.prenom || client?.form?.prenom || 'cliente').trim().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-') || 'cliente';
  const date = String(ligne?.date || ligne?.created_at || '').slice(0, 10) || 'version';
  return `programme-${prenom}-${date}.pdf`;
}

/**
 * Produit le PDF de la version validée, le télécharge et note la remise.
 * `memoire.pdf` garde le PDF déjà produit : « Réessayer » note le MÊME fichier.
 */
export async function remettreDocument({ client, validation, canal, memoire = {} }, {
  db,
  genererPdf = genererPdfParDefaut,
  telecharger = telechargerParDefaut,
} = {}) {
  if (!CANAUX_DOCUMENT.includes(canal)) throw erreurSortie('Choisis comment tu remets le document.');
  const controle = await verifierPublicationPossible({ validationId: validation.id, canal: 'document' }, { db });
  if (controle?.deja_fait === true) return { dejaFait: true };

  if (memoire.pdf?.validationId !== validation.id) {
    const figee = await lireVersion(validation.consultation_id, { db });
    const consultation = normalizeNutritionConsultationCloudRow(ligneTelleQuePubliee(figee), validation.client_id);
    const blob = await genererPdf(consultation, client);
    if (!blob || typeof blob.arrayBuffer !== 'function') throw erreurSortie('Le PDF n’a pas pu être produit.');
    memoire.pdf = {
      validationId: validation.id,
      blob,
      sha256: await sha256Octets(await blob.arrayBuffer()),
      nom: nomFichierPdf(client, figee),
    };
    telecharger(blob, memoire.pdf.nom);
  }

  const notee = await marquerRemiseDocument({
    validationId: validation.id,
    documentSha256: memoire.pdf.sha256,
    format: 'pdf',
    canal,
  }, { db });
  return { dejaFait: false, validation: notee, nom: memoire.pdf.nom };
}

/** Retélécharge le PDF déjà produit (même fichier). */
export function retelechargerPdf(memoire, { telecharger = telechargerParDefaut } = {}) {
  if (!memoire?.pdf?.blob) return false;
  telecharger(memoire.pdf.blob, memoire.pdf.nom);
  return true;
}

/** Ligne suivi_validations (lecture permise à Anissa par RLS). */
export async function lireValidation(validationId, { db = defaultDb } = {}) {
  let reponse;
  try {
    reponse = await db.from('suivi_validations').select('*').eq('id', validationId).maybeSingle();
  } catch {
    throw erreurSortie('Connexion perdue. Réessaie dans un instant.', 'reseau');
  }
  if (reponse?.error || !reponse?.data) throw erreurSortie('La validation est introuvable. Recharge la page.');
  return reponse.data;
}

/** Canaux encore à faire sortir pour une validation. */
export function canauxEnAttente(validation) {
  const canaux = Array.isArray(validation?.canaux) ? validation.canaux : [];
  return canaux.filter((canal) => (canal === 'app' ? !validation.app_plan_id : !validation.document_sha256));
}

/**
 * Programme MGD actif ? Statut illisible = on ne valide pas (on ne sait pas
 * quelles règles appliquer).
 */
export async function programmeMgdActif(clientId, { statut } = {}) {
  const lire = statut || (async (id) => {
    const { mgdReviewV2Client } = await import('../../services/mgdReviewV2Client');
    return mgdReviewV2Client.status(id);
  });
  let etat;
  try {
    etat = await lire(String(clientId || ''));
  } catch {
    etat = null;
  }
  if (!etat || !Array.isArray(etat.reports)) {
    throw erreurSortie('Le statut des analyses MGD est illisible. Réessaie dans un instant.', 'reseau');
  }
  return etat.reports.length > 0;
}
